import { useMemo, useEffect, useState } from "react";
import { Button } from "../ui/button";
import { User, RefreshCw, Wifi, WifiOff } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";

// Custom marker icons
const createIcon = (color, number) => {
  return L.divIcon({
    className: "custom-div-icon",
    html: `<div class="custom-marker ${color === "green" ? "marker-completed" : "marker-pending"}">${number}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
};

const userIcon = L.divIcon({
  className: "custom-div-icon",
  html: `<div class="custom-marker marker-user pulse-animation"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg></div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

// Map updater component - fits all markers in view
function MapUpdater({ userLocation, postcodes }) {
  const map = useMap();
  
  useEffect(() => {
    const points = [];
    
    if (userLocation?.lat && userLocation?.lng) {
      points.push([userLocation.lat, userLocation.lng]);
    }
    
    postcodes?.forEach(pc => {
      if (pc.lat && pc.lng) {
        points.push([pc.lat, pc.lng]);
      }
    });
    
    if (points.length > 0) {
      if (points.length === 1) {
        map.setView(points[0], 13);
      } else {
        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [userLocation, postcodes, map]);
  
  return null;
}

// Freshness indicator for location staleness
function LocationFreshness({ updatedAt }) {
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("text-slate-400");
  const [dotColor, setDotColor] = useState("bg-slate-400");

  useEffect(() => {
    const update = () => {
      if (!updatedAt) {
        setLabel("No location data");
        setColor("text-slate-400");
        setDotColor("bg-slate-400");
        return;
      }

      const now = Date.now();
      const then = new Date(updatedAt).getTime();
      const diffSec = Math.floor((now - then) / 1000);

      if (diffSec < 30) {
        setLabel("Live now");
        setColor("text-emerald-600");
        setDotColor("bg-emerald-500");
      } else if (diffSec < 120) {
        const mins = Math.floor(diffSec / 60);
        setLabel(mins < 1 ? `${diffSec}s ago` : `${mins}m ago`);
        setColor("text-emerald-600");
        setDotColor("bg-emerald-500");
      } else if (diffSec < 600) {
        setLabel(`${Math.floor(diffSec / 60)}m ago`);
        setColor("text-amber-600");
        setDotColor("bg-amber-500");
      } else {
        const mins = Math.floor(diffSec / 60);
        setLabel(mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m ago` : `${mins}m ago`);
        setColor("text-red-500");
        setDotColor("bg-red-500");
      }
    };

    update();
    const interval = setInterval(update, 5000); // refresh every 5s
    return () => clearInterval(interval);
  }, [updatedAt]);

  return (
    <div className="flex items-center gap-2" data-testid="location-freshness">
      <span className={`relative flex h-2.5 w-2.5`}>
        {color === "text-emerald-600" && (
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${dotColor} opacity-75`}></span>
        )}
        <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${dotColor}`}></span>
      </span>
      <span className={`text-sm font-medium ${color}`}>{label}</span>
    </div>
  );
}

export function LiveMapTab({ 
  userLocation, 
  postcodes, 
  routeGeometry, 
  loadingMap, 
  onRefresh 
}) {
  const mapCenter = useMemo(() => {
    if (userLocation?.lat && userLocation?.lng) {
      return [userLocation.lat, userLocation.lng];
    }
    if (postcodes.length > 0 && postcodes[0].lat) {
      return [postcodes[0].lat, postcodes[0].lng];
    }
    return [51.5074, -0.1278];
  }, [userLocation, postcodes]);

  const routePolyline = useMemo(() => {
    if (routeGeometry) {
      return routeGeometry.map(([lng, lat]) => [lat, lng]);
    }
    return [];
  }, [routeGeometry]);

  return (
    <div className="space-y-4">
      {/* Map Header with Freshness + Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {userLocation?.lat ? (
              <Wifi className="w-4 h-4 text-emerald-500" />
            ) : (
              <WifiOff className="w-4 h-4 text-slate-400" />
            )}
            <span className="text-sm font-medium text-slate-700">Driver</span>
          </div>
          <LocationFreshness updatedAt={userLocation?.updated_at} />
        </div>
        <Button
          data-testid="refresh-map-btn"
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={loadingMap}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loadingMap ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Map Container */}
      <div className="h-[450px] rounded-xl overflow-hidden border border-slate-200">
        <MapContainer
          center={mapCenter}
          zoom={12}
          style={{ height: "100%", width: "100%" }}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapUpdater userLocation={userLocation} postcodes={postcodes} />

          {/* Route polyline */}
          {routePolyline.length > 1 && (
            <Polyline
              positions={routePolyline}
              color="#3B82F6"
              weight={4}
              opacity={0.8}
            />
          )}

          {/* User location marker */}
          {userLocation?.lat && (
            <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
              <Popup>
                <div className="text-center">
                  <p className="font-bold">Driver</p>
                  <p className="text-xs text-gray-500">
                    Last update: {userLocation.updated_at ? new Date(userLocation.updated_at).toLocaleString() : 'Unknown'}
                  </p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Postcode markers */}
          {postcodes.map((pc, index) => (
            pc.lat && pc.lng && (
              <Marker
                key={pc.id}
                position={[pc.lat, pc.lng]}
                icon={createIcon(pc.completed ? "green" : "blue", index + 1)}
              >
                <Popup>
                  <div className="text-center">
                    <p className="font-bold">{pc.name}</p>
                    <p className="text-sm text-gray-500">{pc.postcode}</p>
                    <p className={`text-xs mt-1 ${pc.completed ? 'text-green-600' : 'text-blue-600'}`}>
                      {pc.completed ? 'Completed' : 'Pending'}
                    </p>
                  </div>
                </Popup>
              </Marker>
            )
          ))}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-sm text-slate-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-slate-900"></div>
          <span>Driver</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-blue-500"></div>
          <span>Pending</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-green-500"></div>
          <span>Completed</span>
        </div>
      </div>
    </div>
  );
}
